import { createFormContext, zodResolver } from '@mantine/form'
import { AxiosError } from 'axios'
import { useEffect, type HTMLAttributes } from 'react'

import { useAnalytics } from '@flecto/analytics'
import {
  useMutationCreateCustomer,
  useMutationUpdateCustomer,
} from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import {
  type CoreCustomerPayload,
  type CoreCustomer,
  type ParameterID,
} from '@flecto/types'
import { useNotifications, useModal } from '@flecto/ui'

import { type CoreCustomerForm } from '../../types/FormCustomer'
import { useDrawerCustomer } from '../DrawerCustomer/useDrawerCustomer'
import { ModalConfirmChangeDocument } from '../ModalConfirmChangeDocument/ModalConfirmChangeDocument'
import { Address } from './SectionCustomerAddress'
import { BasicInfo } from './SectionCustomerBasicInfo'
import { Discount } from './SectionCustomerDiscount'
import { Identification } from './SectionCustomerIdentification'
import { IdVerifyAt } from './SectionCustomerIdVerifyAt'
import { Language } from './SectionCustomerLanguage'
import { Notes } from './SectionCustomerNotes'
import { useCustomerPayload } from './useCustomerPayload'
import { useCustomerSchema } from './useCustomerSchema'

export const [FormProvider, useFormContext, useForm] =
  createFormContext<CoreCustomerForm>()

type FormCustomerSection =
  | 'basic-info'
  | 'identification'
  | 'verify'
  | 'address'
  | 'language'
  | 'discount'
  | 'notes'

const sectionComponents: Record<FormCustomerSection, React.ComponentType> = {
  'basic-info': BasicInfo,
  identification: Identification,
  verify: IdVerifyAt,
  address: Address,
  language: Language,
  discount: Discount,
  notes: Notes,
}

type CustomerFormProps = HTMLAttributes<HTMLFormElement> & {
  initialCustomer?: Partial<CoreCustomer>
  hideSections?: FormCustomerSection[]
  onSuccess?: (customer: CoreCustomerPayload) => void
  setCustomer?: (customer: Partial<CoreCustomer> | undefined) => void
  onChangeLoading?: (isLoading: boolean) => void
}

export function FormCustomer({
  initialCustomer,
  hideSections = [],
  onSuccess,
  setCustomer,
  onChangeLoading,
  ...props
}: CustomerFormProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'customers' })
  const { mutateAsync: createCustomer } = useMutationCreateCustomer()
  const { mutateAsync: updateCustomer } = useMutationUpdateCustomer()
  const { closeDrawer } = useDrawerCustomer()
  const { showNotification } = useNotifications()
  const { buildPayload } = useCustomerPayload()
  const { schema, initialValues, returnErrorMessage, verifyIdentification } =
    useCustomerSchema({
      customer: initialCustomer,
    })
  const track = useAnalytics('segment')

  const form = useForm({
    initialValues,
    validate: zodResolver(schema),
  })

  const shouldShowSection = (section: FormCustomerSection) => {
    return !hideSections?.includes(section)
  }

  const saveCustomer = async () => {
    try {
      onChangeLoading?.(true)
      const options = {
        onSuccess: (customer: Partial<CoreCustomerPayload>) => {
          onSuccess?.(customer)
          closeDrawer()
          showNotification({
            type: 'success',
            message: t('notifications.customer_saved'),
          })
          setCustomer?.(undefined)
          if (!initialCustomer?.id) {
            track('CUSTOMER__CREATED_ATTEMPT', {
              success: true,
              error: false,
              customer: customer?.email,
            })
          }
        },
      }
      if (!initialCustomer?.id) {
        await createCustomer({ customer: buildPayload(form.values) }, options)
      } else {
        await updateCustomer(
          {
            customer: buildPayload(form.values),
            customerId: initialCustomer?.internal_id as ParameterID,
          },
          options
        )
      }
    } catch (error) {
      showNotification({
        type: 'error',
        message: t('notifications.customer_saved_error'),
      })
      if (error instanceof AxiosError) {
        const errors = returnErrorMessage(error)
        errors.forEach((e) => {
          form.setFieldError(e.field, e.errorMessage)
        })
        if (!initialCustomer?.id) {
          track('CUSTOMER__CREATED_ATTEMPT', {
            success: false,
            error: true,
            errorMessage: Array.isArray(errors)
              ? errors.map((error) => error.errorMessage).join(', ')
              : '',
            customer: initialCustomer?.email,
          })
        }
      }
    } finally {
      onChangeLoading?.(false)
    }
  }

  const { openModal } = useModal(
    <ModalConfirmChangeDocument
      newDocument={form.getInputProps('id_document_number').value}
      newDocumentType={form.getInputProps('id_document_type').value}
      customer={initialCustomer}
      saveCustomer={saveCustomer}
    />
  )

  const handleValidate = () => {
    if (
      initialCustomer?.id &&
      !verifyIdentification(
        form.getInputProps('id_document_number').value,
        form.getInputProps('id_document_type').value
      )
    ) {
      openModal()
    } else {
      saveCustomer()
    }
  }

  useEffect(() => {
    if (initialCustomer) {
      form.setValues(initialCustomer)
    } else {
      form.reset()
    }
  }, [initialCustomer])

  return (
    <FormProvider form={form}>
      <form {...props} onSubmit={form.onSubmit(() => handleValidate())}>
        {Object.entries(sectionComponents).map(
          ([section, Component]) =>
            shouldShowSection(section as FormCustomerSection) && (
              <Component key={section} />
            )
        )}
      </form>
    </FormProvider>
  )
}
